import {
  ReleaseAnnouncementSlide,
  ReleaseModule,
  ReleaseVersion,
  RELEASE_UPDATES_URL,
} from './releases';

const getReleaseTimestamp = (release: ReleaseVersion) => {
  const timestamp = Date.parse(release.releasedAt);
  return Number.isNaN(timestamp) ? 0 : timestamp;
};

export const getReleaseVersion = (release: ReleaseVersion) => {
  const timestamp = Date.parse(release.releasedAt);
  const versionParts = release.releasedAt.match(
    /^(\d{4})-(\d{2})-(\d{2})(?:T(\d{2}):(\d{2}))?/,
  );

  if (Number.isNaN(timestamp) || !versionParts) {
    throw new Error(
      `Invalid release releasedAt "${release.releasedAt}". Use YYYY-MM-DD or YYYY-MM-DDTHH:mm format.`,
    );
  }

  const [, year, month, day, hour = '', minute = ''] = versionParts;
  return Number(`${year}${month}${day}${hour}${minute}`);
};

export const isReleaseVersionSeen = (
  release: ReleaseVersion,
  lastSeenReleaseVersion = 0,
) => lastSeenReleaseVersion >= getReleaseVersion(release);

export const selectUnseenReleaseAnnouncements = (
  releases: ReleaseVersion[],
  lastSeenReleaseVersion = 0,
  releaseModule: ReleaseModule,
) => {
  return releases
    .filter((release) => !isReleaseVersionSeen(release, lastSeenReleaseVersion))
    .sort((a, b) => {
      const versionDiff = getReleaseVersion(b) - getReleaseVersion(a);
      if (versionDiff !== 0) return versionDiff;

      const dateDiff = getReleaseTimestamp(b) - getReleaseTimestamp(a);
      if (dateDiff !== 0) return dateDiff;

      return 0;
    })
    .flatMap<ReleaseAnnouncementSlide>((release) =>
      [...release.announcements]
        .filter((announcement) => announcement.modules.includes(releaseModule))
        .sort((a, b) => {
          const priorityDiff = (a.priority ?? 0) - (b.priority ?? 0);
          if (priorityDiff !== 0) return priorityDiff;

          return a.id.localeCompare(b.id);
        })
        .map((announcement) => ({
          ...announcement,
          releaseVersion: getReleaseVersion(release),
          releasedAt: release.releasedAt,
          updatesUrl: release.updatesUrl ?? RELEASE_UPDATES_URL,
        })),
    );
};

export const markReleaseAnnouncementsSeen = (
  currentLastSeenReleaseVersion = 0,
  releases: ReleaseAnnouncementSlide[],
) =>
  releases.reduce(
    (highestSeenVersion, release) =>
      Math.max(highestSeenVersion, release.releaseVersion),
    currentLastSeenReleaseVersion,
  );
